import { themeColors } from "../../../themeColors";
/**
 * @file   src\components\AgentDashboard\InventoryListing.js
 * @brief  This file is responsible for Inventory listing in agent dashboard section
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React from "react";
import {
  formateDate,
  loadPropertTypeImage,
  thousandSeperatedValue,
  setLocalStorage,
  truncate,
} from "../../../helpers/common";
import { INVENTORY_KEY } from "../../../constants/common";
import ActionDropdown from "../ActionDropdown";
import { GenericTable } from "@/components";
import TableHeader from "../TableHeader";
import potentialSellerPlaceholder from "../../../images/potential-seller-placeholder.svg";
import { pageURLs } from "../../../constants/pageURLs";
import { useNavigate } from "react-router-dom";
import { formatInventoryAddress } from "@/helpers";
import { emptyPropertyPlaceholderImages } from "@/components/Onboarding/Dashboard/v2/PropertyInventoryListing";

const InventoryListing = (props) => {
  const navigate = useNavigate();
  const {
    showNoResult,
    handleSort,
    itemList,
    onItemEdit,
    onItemDelete,
    sort,
    fromInvestorTab,
    loading,
  } = props;

  // Sort so potential sellers always appear at the bottom, preserving
  // the existing order within each group.
  const sortedItems = React.useMemo(() => {
    if (!itemList || itemList.length === 0) return itemList;
    const active = [];
    const potential = [];
    itemList.forEach((item) => {
      if (_.get(item, "Represent_as_Seller", 0) === 1) {
        potential.push(item);
      } else {
        active.push(item);
      }
    });
    return [...active, ...potential];
  }, [itemList]);

  const columns = [
    {
      label: "Date",
      sortKey: "date",
      sorting: "Yes",
      cell: (inventry) => {
        const createdOn = _.get(inventry, "CreatedOn", "");
        return <span style={{ whiteSpace: 'nowrap' }}>{formateDate(createdOn, "mm/dd/yy")}</span>;
      },
    },
    {
      label: "Address",
      sortKey: "address",
      sorting: "Yes",
      cell: (_inventory) => {
        const photo = _.get(_inventory, "photo", null);
        const isPotentialSeller = _.get(_inventory, "Represent_as_Seller", 0) === 1;

        const emptyPlaceholderImage = emptyPropertyPlaceholderImages[Math.floor(Math.random() * emptyPropertyPlaceholderImages.length)];
        const emptyPlaceholderImageUrl = require(`@/images/${emptyPlaceholderImage}`);

        // Potential sellers have no photos - agents can't upload to manual inventory
        const invPhoto = isPotentialSeller ? potentialSellerPlaceholder : (photo || emptyPlaceholderImageUrl);

        const location = _.get(_inventory, "Location", "");

        return location ? (
          <a
            href={void 0}
            role="button"
            className="txt-no-underline client-name-style"
          >
            <div className="d-flex align-items-center justify-content-start">
              <div className="table-address-img">
                <img src={invPhoto} alt="inventoryImage" />
              </div>
              <span className="d-inline-block client-name-style txt-overflow-style">
                {truncate(formatInventoryAddress(location), 35)}
              </span>
            </div>
          </a>
        ) : null;
      },
    },
    {
      label: "Potential Buyers",
      sortKey: "potentialbuyers",
      sorting: "Yes",
      cell: (inventory) => {
        const potentialBuyers = _.get(inventory, "potentialBuyers", "");
        const invId = _.get(inventory, "Inv_Id", "");
        const hasPotentialBuyers = potentialBuyers > 0;
        const representingAsSeller = _.get(
          inventory,
          "Represent_as_Seller",
          "",
        );

        const color = representingAsSeller ? "green" : "blue";

        // Navigate to feed Match tab with this property pre-selected for demand analytics
        const handleNavigateToMatches = (e) => {
          if (!hasPotentialBuyers) return;
          e.stopPropagation();

          setLocalStorage("FilterTabKey", "match");
          setLocalStorage("selectedPropertyId", { Inv_Id: invId });
          navigate(`${pageURLs?.home || "/home"}?InvId=${btoa("inv-" + invId)}`);
        };

        return (
          <div
            onClick={handleNavigateToMatches}
            className={
              hasPotentialBuyers
                ? `want-ad-tag medium-${color}-tag-bg-border medium-${color}-tag-bg-border-clickable mb-0`
                : ""
            }
          >
            {thousandSeperatedValue(potentialBuyers)} Matches
          </div>
        );
      },
    },
    {
      label: "Price",
      sortKey: "price",
      sorting: "Yes",
      cell: (inventry) => {
        const price = _.get(inventry, "AskingPrice_Sell", 0);
        // [US-012] Show estimated price for potential sellers
        const isPotentialSeller = _.get(inventry, "Represent_as_Seller", "");
        const formattedPrice = Number(price).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });

        return (
          <p className="mb-0" style={{ whiteSpace: 'nowrap', ...(isPotentialSeller ? { fontStyle: "italic" } : {}) }}>
            {isPotentialSeller ? `Est. ${formattedPrice}` : formattedPrice}
          </p>
        );
      },
    },
    {
      label: "Property Status",
      sortKey: "property_status",
      sorting: "Yes",
      cell: (inventry) => {
        const isDraft = _.get(inventry, "IsSavedDraft", "") === 1;
        const isSold = _.get(inventry, "IsSold", "") === 1;
        // [US-012] Show "Potential Seller" status for manually-loaded inventory
        const isPotentialSeller = _.get(inventry, "Represent_as_Seller", "");

        let status = "Active";
        if (isDraft) status = "Draft";
        else if (isSold) status = "Sold";
        else if (isPotentialSeller) status = "Prospect";

        return <p style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>{status}</p>;
      },
    },
    {
      label: "Type",
      sortKey: "type",
      sorting: "Yes",
      cell: (inventry) => {
        const representingAsSeller = _.get(inventry, "Represent_as_Seller", "");
        return (
          <div
            className="d-flex align-items-center justify-content-start"
            style={{
              backgroundColor: `${representingAsSeller ? themeColors.accentf100 : themeColors.secondary120}`,
              width: "fit-content",
              paddingRight: "5px",
              paddingLeft: "5px",
              whiteSpace: "nowrap",
            }}
          >
            <div className="property-location-icon">
              {loadPropertTypeImage(representingAsSeller)}
            </div>
            <span
              style={{
                color: `${representingAsSeller ? themeColors.accentf600 : themeColors.secondary600}`,
                fontWeight: 500,
              }}
            >
              {representingAsSeller ? "PROSPECT" : "SELLER"}
            </span>
          </div>
        );
      },
    },
    {
      label: "Client",
      sorting: "Yes",
      sortKey: "client",
      cell: (inventry) => {
        if (fromInvestorTab) return null;
        const firstName = _.get(inventry, "FirstName", null);
        const lastName = _.get(inventry, "LastName", null);
        const isAgent = _.get(inventry, "Represent_as_Seller", "") == 1;
        const invId = _.get(inventry, "Inv_Id", "");

        const clientName =
          firstName || lastName
            ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
            : "";

        const client = clientName;

        return (
          <div className="agent-want-listing-dropdown" onClick={(e) => e.stopPropagation()}>
            <p className="mb-0">{client}</p>
            <div className="social-btns-wrap no-bg-toggle ms-1">
              <ActionDropdown
                onItemEdit={onItemEdit}
                onItemDelete={onItemDelete}
                editText="Edit"
                deleteText="Delete"
                itemId={invId}
                buyerListLinkText="BuyerListing Form"
                onGenerateReport={(id) => navigate(`/reports/active-buyer/${id}`)}
                generateReportText="Generate Active Buyer Report"
              />
            </div>
          </div>
        );
      },
    },
  ];

  /**
   * To handle inventory item click
   * @param {*} inventoryId
   * @param {string} tabKey - Optional tab key to navigate to specific tab
   */
  const handleInventoryItemClick = (inventoryId, tabKey = null, isPublished) => {

    if (!isPublished) {
      return onItemEdit(inventoryId);
    }

    setLocalStorage(INVENTORY_KEY, {
      inventoryId: inventoryId,
      isFromAgentDashboard: true,
    });
    const url = tabKey
      ? `${pageURLs.inventoryDetails}?tabKey=${tabKey}`
      : pageURLs.inventoryDetails;
    navigate(url);
  };

  return (
    <div className="dashboard-table-wrap">
      <div className="table-responsive" style={{ overflowX: 'visible' }}>
        {showNoResult && sortedItems !== null && sortedItems?.length > 0 ? (
          <GenericTable
            data={sortedItems}
            columns={columns}
            className="table"
            renderHeader={() => (
              <TableHeader
                columns={columns}
                handleSort={handleSort}
                sort={sort}
              />
            )}
            renderRow={(item) =>
              columns.map((col, idx) => (
                <td key={idx} style={{ cursor: 'pointer' }} onClick={() => handleInventoryItemClick(item.Inv_Id, null, item.IsPublished)}>{col.cell(item)}</td>
              ))
            }
          />
        ) : (
          !loading &&
          showNoResult &&
          (itemList == null || itemList?.length == 0) && (
            <div className="no-result-wrap no-result-padding">
              <h3>No results found</h3>
              <p>
                We couldn't find any listings that match your filters and search
                terms. Try a different search or reset all filters.
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
};
export default InventoryListing;
